import {SessionCoordinator, sessionEnded, accessExpired} from './sessionCoordinator';
import { decryptMsg, encryptMsg } from '../../im/security';
import { defaultApi, httpCryptoAes, httpCryptoEnabled } from '../config/runtime';

const cryptoProfile = 'locs-im-v1';
const cryptoHeader = 'X-Locs-Crypto';
const cryptoPrefix = `${cryptoProfile}.`;
const cryptoBodyField = 'payload';

const defaultBase = () => defaultApi;

const normalizeBaseUrl = (baseUrl: string) => baseUrl.replace(/\/$/, '');
const normalizePath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

export type ApiEnvelope<T> = {
  status?: string;
  statusCode?: number;
  message?: string;
  data?: T;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
    public readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isUnauthorizedError(error: unknown): boolean {
  return sessionEnded(error);
}

function getErrorMessage(status: number, body: unknown, statusText: string) {
  if (typeof body === 'object' && body && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return statusText || `Request failed with status ${status}`;
}

function encryptBody(body: BodyInit | null | undefined): BodyInit | null | undefined {
  if (body == null || typeof body !== 'string') {
    return body;
  }
  if (!httpCryptoAes) {
    throw new Error('LOCS_HTTP_CRYPTO_AES_KEY must be configured when HTTP crypto is enabled');
  }

  try {
    const parsed = JSON.parse(body) as unknown;
    return JSON.stringify({
      [cryptoBodyField]: `${cryptoPrefix}${encryptMsg(JSON.stringify(parsed), httpCryptoAes)}`,
    });
  } catch {
    return body;
  }
}

function decryptBody(body: unknown): unknown {
  if (!body || typeof body !== 'object' || !(cryptoBodyField in body)) {
    return body;
  }
  if (!httpCryptoAes) {
    throw new Error('LOCS_HTTP_CRYPTO_AES_KEY must be configured when HTTP crypto is enabled');
  }

  const transportBody = body as Record<string, unknown>;
  if (
    typeof transportBody[cryptoBodyField] !== 'string' ||
    !transportBody[cryptoBodyField].startsWith(cryptoPrefix)
  ) {
    return body;
  }

  const decryptedBody = JSON.parse(
    decryptMsg(transportBody[cryptoBodyField].slice(cryptoPrefix.length), httpCryptoAes),
  ) as unknown;
  return decryptedBody;
}

async function rawRequestJson<T>(
  path: string,
  init: RequestInit & {
    baseUrl?: string;
    /**
     * Observe status and headers without taking ownership of the response
     * stream. Profile APIs use this to keep the canonical ETag beside the body
     * snapshot used for optimistic concurrency.
     */
    onResponse?: (response: Response) => void;
  } = {},
): Promise<T> {
  const { baseUrl = defaultBase(), headers, onResponse, ...rest } = init;
  const target = new URL(`${normalizeBaseUrl(baseUrl)}${normalizePath(path)}`, window.location.origin);
  const api = new URL(`${normalizeBaseUrl(defaultBase())}/`, window.location.origin);
  if (target.origin !== api.origin || !target.pathname.startsWith(api.pathname)) {
    throw new Error('请求地址不属于当前服务');
  }
  const requestHeaders = new Headers(headers);
  const encryptedBody = httpCryptoEnabled ? encryptBody(rest.body) : rest.body;
  if (httpCryptoEnabled) {
    requestHeaders.set(cryptoHeader, cryptoProfile);
  }
  if (encryptedBody !== rest.body && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }
  const response = await fetch(
    `${normalizeBaseUrl(baseUrl)}${normalizePath(path)}`,
    {
      credentials: 'include',
      ...rest,
      signal: rest.signal ? AbortSignal.any([rest.signal, AbortSignal.timeout(15000)]) : AbortSignal.timeout(15000),
      body: encryptedBody,
      headers: requestHeaders,
    },
  );
  onResponse?.(response);

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      const parsed = JSON.parse(text) as unknown;
      body = httpCryptoEnabled ? decryptBody(parsed) : parsed;
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    throw new ApiError(
      getErrorMessage(response.status, body, response.statusText),
      response.status,
      body,
      /^\d+$/.test(response.headers.get('retry-after') || '') ? Number(response.headers.get('retry-after')) * 1000 : undefined,
    );
  }

  return body as T;
}

const coordinators = new Map<string, SessionCoordinator>();
export function sessionCoordinator(baseUrl = defaultBase()) {
  const base = normalizeBaseUrl(baseUrl);
  let coordinator = coordinators.get(base);
  if (!coordinator) {
    coordinator = new SessionCoordinator(base, (path, body) => rawRequestJson(path, {
      baseUrl: base, signal: AbortSignal.timeout(15000), method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(body),
    }));
    coordinators.set(base, coordinator);
  }
  return coordinator;
}
export async function requestJson<T>(path: string, init: Parameters<typeof rawRequestJson<T>>[1] = {}): Promise<T> {
  const route = normalizePath(path).replace(/\/$/, '');
  const coordinator = sessionCoordinator(init.baseUrl);
  if (route === '/auth/login' || route === '/auth/oauth/callback') {
    return coordinator.login(() => rawRequestJson<T>(path, init));
  }
  if (route === '/auth/logout') { await coordinator.logout(); return {status:'Success',data:true} as T; }
  const authRoute = /^\/auth\/(registration|password-reset|refresh)(\/|$)/.test(route);
  const publicRoute = (['GET','HEAD'].includes((init.method || 'GET').toUpperCase()) && (route.startsWith('/content/config/') || route.startsWith('/content/article') || route.startsWith('/app-config') || route.startsWith('/public/') || route.startsWith('/discovery/')));
  if (authRoute || (publicRoute && coordinator.read()?.status !== 'active')) return rawRequestJson<T>(path, init);
  await coordinator.ensure();
  if (coordinator.read()?.status !== 'active') throw new ApiError('Session changed',401,{data:{code:'LOCAL_SIGNED_OUT'}});
  const epoch = coordinator.read()?.epoch;
  const expiry = coordinator.read()?.expiresAt;
  init.signal?.throwIfAborted();
  try {
    const result = await rawRequestJson<T>(path, init);
    if (coordinator.read()?.epoch !== epoch) throw new ApiError('Session changed',401,{data:{code:'LOCAL_SIGNED_OUT'}});
    return result;
  } catch (error) {
    if (coordinator.read()?.epoch !== epoch) throw error;
    if (accessExpired(error)) {
      await coordinator.ensure(true, expiry);
      if (coordinator.read()?.epoch !== epoch || coordinator.read()?.status !== 'active') throw new ApiError('Session changed',401,{data:{code:'LOCAL_SIGNED_OUT'}});
      init.signal?.throwIfAborted();
      // Only read requests replay automatically; writes are renewed before dispatch.
      if (['GET','HEAD'].includes((init.method || 'GET').toUpperCase())) {
        const retryEpoch = coordinator.read()?.epoch;
        const result = await rawRequestJson<T>(path, init).catch(retryError => { if (coordinator.read()?.epoch === retryEpoch) coordinator.invalidate(retryError); throw retryError; });
        if (coordinator.read()?.epoch !== retryEpoch) throw new ApiError('Session changed',401,{data:{code:'LOCAL_SIGNED_OUT'}});
        return result;
      }
    }
    coordinator.invalidate(error);
    throw error;
  }
}
